import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface CursorPage<T> {
  items: T[];
  nextCursor: number | null;
  hasMore: boolean;
}

/**
 * Cursor-paginated, server-searched list backed by React Query's infinite query.
 * Designed for large tables (no expensive total count) with a "Load more" UX.
 *
 * `fetchPage` receives the cursor + page size and must return { items, nextCursor, hasMore }.
 * Put the (debounced) search term + filters into `key` so changing them resets the list.
 */
export function useCursorList<T>(opts: {
  key: unknown[];
  pageSize?: number;
  enabled?: boolean;
  /** React Query meta, e.g. { suppressErrorToast: true } for auto-refreshing pages. */
  meta?: Record<string, unknown>;
  fetchPage: (args: { cursor?: number; limit: number }) => Promise<CursorPage<T>>;
}) {
  const pageSize = opts.pageSize ?? 25;
  const query = useInfiniteQuery({
    queryKey: opts.key,
    enabled: opts.enabled ?? true,
    meta: opts.meta,
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => opts.fetchPage({ cursor: pageParam, limit: pageSize }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: (prev) => prev,
  });

  const rows = useMemo<T[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  return {
    rows,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}

/** Normalize the backend's `{ data, meta:{ nextCursor, hasNextPage } }` shape into a CursorPage. */
export function toCursorPage<T>(resData: any): CursorPage<T> {
  const items: T[] = resData?.data ?? resData?.items ?? (Array.isArray(resData) ? resData : []);
  const nextCursor = resData?.meta?.nextCursor ?? null;
  const hasMore = resData?.meta?.hasNextPage ?? nextCursor != null;
  return { items, nextCursor, hasMore };
}
